import { AuthenticationDetails, CognitoUser, CognitoUserPool, type CognitoUserSession } from 'amazon-cognito-identity-js';
import { setAuthState, authStore, signOutLocally } from './store';
import { safeStorage } from '../store';

export const COGNITO_REGION = import.meta.env.VITE_COGNITO_REGION ?? 'eu-central-1';

let pool: CognitoUserPool | undefined;

export function isCognitoConfigured(): boolean {
  return Boolean(import.meta.env.VITE_COGNITO_USER_POOL_ID && import.meta.env.VITE_COGNITO_CLIENT_ID);
}

function getPool(): CognitoUserPool {
  if (pool) return pool;
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!UserPoolId || !ClientId) throw new Error('Cognito is not configured (VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID)');
  // Tokens live in sessionStorage (per tab) rather than the library default localStorage.
  const Storage = safeStorage('session');
  pool = new CognitoUserPool(Storage ? { UserPoolId, ClientId, Storage } : { UserPoolId, ClientId });
  return pool;
}

function applySession(username: string, session: CognitoUserSession): void {
  const idToken = session.getIdToken();
  setAuthState({ status: 'cognito', idToken: idToken.getJwtToken(), expiresAt: idToken.getExpiration() * 1000, username });
}

export class NewPasswordRequiredError extends Error {
  constructor() {
    super('A new password is required for this account. Complete the password change in the Cognito console or CLI first.');
    this.name = 'NewPasswordRequiredError';
  }
}

/** SRP sign-in. Resolves once an ID token is in the auth store. */
export function signInWithCognito(username: string, password: string): Promise<void> {
  const user = new CognitoUser({ Username: username, Pool: getPool() });
  const details = new AuthenticationDetails({ Username: username, Password: password });
  return new Promise<void>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        applySession(username, session);
        resolve();
      },
      onFailure: (err: unknown) => reject(err instanceof Error ? err : new Error(String(err))),
      newPasswordRequired: () => reject(new NewPasswordRequiredError()),
    });
  });
}

/**
 * Returns a fresh ID token, refreshing through the refresh token when the current one is within
 * `skewMs` of expiry. Signs the user out locally when refresh fails (session expiry).
 */
export async function ensureFreshIdToken(skewMs = 60_000): Promise<string | undefined> {
  const state = authStore.get();
  if (state.status !== 'cognito') return undefined;
  if (state.expiresAt - Date.now() > skewMs) return state.idToken;
  if (!isCognitoConfigured()) return state.idToken;
  const current = getPool().getCurrentUser();
  if (!current) {
    signOutLocally('expired');
    return undefined;
  }
  return new Promise<string | undefined>((resolve) => {
    // getSession() transparently refreshes an expired session using the stored refresh token.
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        signOutLocally('expired');
        resolve(undefined);
        return;
      }
      applySession(state.username, session);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function signOutCognito(): void {
  try {
    if (isCognitoConfigured()) getPool().getCurrentUser()?.signOut();
  } catch {
    /* ignore */
  }
}
